import { Injectable, NgZone, ApplicationRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

/**
 * A wrapper service for ToastrService that runs outside Angular's zone
 * to prevent NG0100: ExpressionChangedAfterItHasBeenCheckedError
 */
@Injectable({
    providedIn: 'root'
})
export class ToastService {
    constructor(
        private toastr: ToastrService,
        private ngZone: NgZone,
        private appRef: ApplicationRef
    ) {}

    private showToast(method: 'success' | 'error' | 'warning' | 'info', message: string, title?: string): void {
        // Wait for change detection to complete, then show toast outside Angular
        this.appRef.isStable.subscribe(isStable => {
            if (isStable) {
                this.ngZone.runOutsideAngular(() => {
                    // Use requestAnimationFrame to ensure we're outside the current change detection cycle
                    requestAnimationFrame(() => {
                        this.ngZone.run(() => {
                            this.toastr[method](message, title);
                        });
                    });
                });
            }
        }).unsubscribe();
        
        // Fallback: also schedule with setTimeout in case the app is already stable
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.toastr[method](message, title);
                });
            }, 100);
        });
    }

    success(message: string, title?: string): void {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.toastr.success(message, title);
                });
            }, 50);
        });
    }

    error(message: string, title?: string): void {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.toastr.error(message, title);
                });
            }, 50);
        });
    }

    warning(message: string, title?: string): void {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.toastr.warning(message, title);
                });
            }, 50);
        });
    }

    info(message: string, title?: string): void {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.toastr.info(message, title);
                });
            }, 50);
        });
    }
}
